import { Controller } from '@hotwired/stimulus'

// Google OAuth popup login Stimulus controller
export default class extends Controller {
  connect() {
    // Detect if running inside an iframe
    this.isEmbedded = window.self !== window.top
    // Bind postMessage listener
    this._onMessage = this._onMessage.bind(this)
    window.addEventListener('message', this._onMessage)
    this.popupWindow = null
    this._popupPollTimeout = null

    // Read allowed origins from meta tag
    const meta = document.querySelector('meta[name="oauth-origins"]')
    this.allowedOrigins = meta ? meta.content.split(',').map((s) => s.trim()) : []
  }

  // login action
  login(event) {
    // Find the OAuth URL from the triggering element (e.g., data-url or href)
    const url = event.currentTarget.dataset.url || event.currentTarget.href
    if (!url) {
      console.error('No OAuth URL provided on the button/link.')
      return
    }

    if (window.DD_RUM) {
      window.DD_RUM.addAction('login_attempt', {
        login_method: 'google_oauth',
        login_context: this.isEmbedded ? 'healthie_iframe' : 'direct',
      })
    }

    if (this.isEmbedded) {
      // Hybrid approach: open popup to /oauth/popup_proxy, then send form via postMessage (Safari/Firefox compatibility)
      const popupWindow = window.open(
        '/oauth/popup_proxy',
        'oauth_popup',
        'width=600,height=700,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes'
      )
      // Listen for popup ready
      const popupReadyHandler = (event) => {
        if (event.data && event.data.type === 'OAUTH_POPUP_READY') {
          const csrfToken = document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content')
          // Get CSP nonce from the proxy page
          let nonce = null
          try {
            nonce = popupWindow.document
              .querySelector('meta[name="csp-nonce"]')
              ?.getAttribute('content')
          } catch (_e) {
            // Accessing popupWindow.document may fail due to timing; fallback to window's own nonce
            nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content')
          }
          const nonceAttr = nonce ? ` nonce="${nonce}"` : ''
          const formHtml = `
            <form id="oauthForm" method="post" action="${url || '/users/auth/google_oauth2'}">
              <input type="hidden" name="authenticity_token" value="${csrfToken}" />
            </form>
            <script${nonceAttr}>document.getElementById('oauthForm').submit();</script>
          `
          popupWindow.postMessage(
            { type: 'OAUTH_POST_FORM', html: formHtml },
            window.location.origin
          )
          window.removeEventListener('message', popupReadyHandler)
        }
      }
      window.addEventListener('message', popupReadyHandler)
      this.popupWindow = popupWindow
      this._startPopupPoll()
      return
    }
    if (!this.isEmbedded) {
      // Not embedded: fallback to redirect
      const form = document.createElement('form')
      form.method = 'post'
      form.action = '/users/auth/google_oauth2'
      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'authenticity_token'
        input.value = csrfToken
        form.appendChild(input)
      }
      document.body.appendChild(form)
      form.submit()
      return
    }
    event.preventDefault()
    // Open popup window and submit a POST form to /users/auth/google_oauth2 (same as non-embedded)
    const popupWindow = window.open(
      '',
      'oauth_popup',
      'width=600,height=700,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes'
    )
    const csrfToken =
      document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    // Compose HTML for the popup
    const formHtml = `
      <form id="oauthForm" method="post" action="/users/auth/google_oauth2">
        <input type="hidden" name="authenticity_token" value="${csrfToken}" />
      </form>
      <script>document.getElementById('oauthForm').submit();</script>
    `
    popupWindow.document.write(formHtml)
    popupWindow.document.close()
    this.popupWindow = popupWindow
  }

  // Handle postMessage from popup
  _onMessage(event) {
    // Only accept messages from allowed origins
    if (!this.allowedOrigins.includes(event.origin)) return
    const data = event.data
    if (data && data.status === 'success') {
      // Handle success (e.g., reload, show message, etc.)
      // Example: window.location.reload();
    } else if (data && data.status === 'error') {
      // Handle error (e.g., show error message)
    }
    // Close popup if open
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close()
      this.popupWindow = null
    }
  }

  disconnect() {
    window.removeEventListener('message', this._onMessage)
  } // Polls the popup window to detect when it returns to our app's root

  // Polls the popup window to detect when it returns to our app's root
  _startPopupPoll() {
    const poll = () => {
      if (!this.popupWindow || this.popupWindow.closed) {
        // Popup closed by user or finished
        this._popupPollTimeout = null
        return
      }
      try {
        // If popup is at the same origin and root path
        if (
          this.popupWindow.location.hostname === window.location.hostname &&
          this.popupWindow.location.pathname === '/'
        ) {
          this.popupWindow.close()
          this._popupPollTimeout = null
          // Focus parent and navigate iframe to root
          window.location = '/'
          return
        }
      } catch (_e) {
        // Cross-origin error, ignore and continue polling
      }
      this._popupPollTimeout = setTimeout(poll, 500)
    }
    poll()
  }
}
